/**
 * Calendar maths on plain `YYYY-MM-DD` strings. Classes are stored as gym-local
 * wall clock, so nothing here needs a timezone: a day is a day.
 */

export function addDays(day: string, days: number): string {
  const d = new Date(`${day}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/** ISO weekday: 1 = Monday ... 7 = Sunday. */
export function isoWeekday(day: string): number {
  return new Date(`${day}T12:00:00Z`).getUTCDay() || 7
}

/** The Monday of the week `day` falls in. */
export function mondayOf(day: string): string {
  return addDays(day, 1 - isoWeekday(day))
}

/** The seven days starting on `monday`. */
export function weekDays(monday: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i))
}

/** "17:00" from a wall-clock timestamp. */
export function timeOf(wallClock: string): string {
  return wallClock.slice(11, 16)
}

/** Today in the gym's zone, falling back to the browser's. */
export function gymToday(timezone: string | null): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone ?? undefined }).format(new Date())
}

/** "Mon 19" / "lun. 19", in the reader's language. */
export function dayLabel(day: string, locale: string): { weekday: string; date: string } {
  const d = new Date(`${day}T12:00:00Z`)
  return {
    weekday: new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(d),
    date: new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    }).format(d),
  }
}

/** "19 – 25 Oct 2026", the week header. */
export function weekTitle(monday: string, locale: string): string {
  const fmt = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
  return fmt.formatRange(
    new Date(`${monday}T12:00:00Z`),
    new Date(`${addDays(monday, 6)}T12:00:00Z`),
  )
}

/** The age band as people say it: "5–16", "18+", "up to 12", or null for everyone. */
export function ageBand(minAge: number | null, maxAge: number | null): string | null {
  if (minAge === null && maxAge === null) return null
  if (minAge !== null && maxAge !== null) return `${minAge}–${maxAge}`
  if (minAge !== null) return `${minAge}+`
  return `≤ ${maxAge}`
}

/** The first day of the month `day` falls in. */
export function firstOfMonth(day: string): string {
  return `${day.slice(0, 7)}-01`
}

/** The first of the month `months` away, on the calendar. */
export function addMonths(day: string, months: number): string {
  const d = new Date(`${firstOfMonth(day)}T12:00:00Z`)
  d.setUTCMonth(d.getUTCMonth() + months)
  return d.toISOString().slice(0, 10)
}

/** Six whole weeks from the Monday on or before the 1st: every month fits, always the same height. */
export function monthGrid(day: string): string[] {
  const start = mondayOf(firstOfMonth(day))
  return Array.from({ length: 42 }, (_, i) => addDays(start, i))
}

/** "October 2026" / "octombrie 2026". */
export function monthTitle(day: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${firstOfMonth(day)}T12:00:00Z`))
}

/** Short weekday names, Monday first. */
export function weekdayNames(locale: string): string[] {
  // 2026-10-12 is a Monday.
  return weekDays('2026-10-12').map((d) => dayLabel(d, locale).weekday)
}
